import { useQueryClient } from '@tanstack/react-query';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ModalConfirmation } from '@/components';
import { useHeroToast } from '@/hooks';

import {
  KdsDevice, kdsDevicesQueryKey, kdsErrorKey, useUpdateKdsDevice,
} from '../../queries';
import {
  buildKdsUpdateRequest, hasDeletedKdsStation, initialKdsAssignment, KdsAssignmentFields, KdsAssignmentValue,
} from '../KdsAssignmentFields';

interface EditKdsDeviceModalProps {
  hotelId: string;
  device: KdsDevice;
  roomServices: { id: string; name: string }[];
  onClose: () => void;
}

/**
 * Zmiana punktu i stanowiska sparowanego ekranu (`v3-updateKdsDevice`), bez ponownego
 * parowania. Wysyłamy tylko pola, które się zmieniły; null czyści przypisanie, w tym
 * stanowisko usunięte w room service.
 */
export const EditKdsDeviceModal: FC<EditKdsDeviceModalProps> = ({
  hotelId, device, roomServices, onClose,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showError, showSuccess } = useHeroToast();
  const updateDevice = useUpdateKdsDevice();

  const hasDeletedStation = hasDeletedKdsStation(device);
  const [value, setValue] = useState<KdsAssignmentValue>(() => initialKdsAssignment(device));
  const request = buildKdsUpdateRequest(hotelId, device, value, roomServices);
  const hasChanges = request !== null;

  const errorMessage = (key: string): string => {
    switch (key) {
      case 'kdsDevice.roomServiceNotFound':
      case 'kdsDevice.invalidRoomServiceId':
        return t('myDevices.kds.errors.roomServiceNotFound', 'Wybrany punkt gastronomiczny nie istnieje.');
      case 'kdsDevice.invalidGastronomyPointId':
        return t('myDevices.kds.errors.invalidGastronomyPointId', 'Wybrane stanowisko nie istnieje w tym punkcie.');
      case 'kdsDevice.notFound':
      case 'kdsDevice.invalidDeviceId':
        return t('myDevices.kds.errors.deviceNotFound', 'Ten ekran nie jest już sparowany z hotelem. Odśwież listę.');
      case 'kdsDevice.deviceForbidden':
        return t('myDevices.kds.errors.deviceForbidden', 'Ten ekran należy do innego hotelu.');
      case 'kdsDevice.nothingToUpdate':
        return t('myDevices.kds.errors.nothingToUpdate', 'Nic się nie zmieniło.');
      default:
        return t('myDevices.kds.errors.updateFailed', 'Nie udało się zmienić przypisania ekranu.');
    }
  };

  const handleSave = () => {
    if (!request) return;
    updateDevice.mutate(request, {
      onSuccess: () => {
        showSuccess(t('myDevices.kds.updateSuccess', 'Przypisanie ekranu zostało zmienione.'));
        queryClient.invalidateQueries({ queryKey: kdsDevicesQueryKey(hotelId) });
        onClose();
      },
      onError: (err) => {
        showError(errorMessage(kdsErrorKey(err)));
        // Ekran zniknął albo stanowisko usunięto w międzyczasie: odświeżamy listę, modal zostaje.
        queryClient.invalidateQueries({ queryKey: kdsDevicesQueryKey(hotelId) });
      },
    });
  };

  return (
    <ModalConfirmation
      isOpen
      tone="primary"
      footerDivider
      isLoading={updateDevice.isPending}
      isConfirmDisabled={!hasChanges}
      itemId={device.deviceId}
      onClose={onClose}
      onConfirm={handleSave}
      title={String(t('myDevices.kds.editTitle', 'Zmień punkt i stanowisko'))}
      description={t(
        'myDevices.kds.editDescription',
        'Tablet „{{name}}” przełączy się sam, bez ponownego parowania.',
        { name: device.name || t('myDevices.kds.unnamed', 'Ekran KDS') },
      )}
      buttonConfirm={String(t('common.save', 'Zapisz'))}
    >
      <div className="flex flex-col gap-5 pt-3">
        <KdsAssignmentFields
          hotelId={hotelId}
          idPrefix="kds-edit"
          roomServices={roomServices}
          value={value}
          onChange={setValue}
          isDisabled={updateDevice.isPending}
          hasDeletedStation={hasDeletedStation}
          showEmptyHint
        />
      </div>
    </ModalConfirmation>
  );
};
